import { PortalContainerContext } from "@istari-frontend/shared";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  FileText,
  KeyRound,
  PanelBottom,
  PanelRight,
  Sparkles,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";

import { openLlmKeyPrompt, useLlmApiKey } from "~/ai-chat/data-source/llm-api-key-store";
import { AiChatComposer } from "~/ai-chat/feature/ai-chat-composer";
import { AiChatHistorySidebar } from "~/ai-chat/feature/ai-chat-history-sidebar";
import { AiChatTranscript } from "~/ai-chat/feature/ai-chat-transcript";
import { ChatSaveShareControls } from "~/ai-chat/feature/chat-save-share-controls";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { AI_CHAT_DOCK_TRANSITION_MS, useAiChatWindowSize } from "~/ai-chat/feature/use-ai-chat-window-size";
import type { ChatDockPosition } from "~/ai-chat/feature/use-chat-dock-position";
import { useChatDockPosition } from "~/ai-chat/feature/use-chat-dock-position";
import { useChatPopout } from "~/ai-chat/feature/use-chat-popout";
import { useChatPopoutMode } from "~/ai-chat/feature/use-chat-popout-mode";
import { useChatSend } from "~/ai-chat/feature/use-chat-send";
import { useChatTabs } from "~/ai-chat/feature/use-chat-tabs";
import { useConversationArtifacts } from "~/ai-chat/feature/use-conversation-artifacts";
import { useHydrateActiveConversation } from "~/ai-chat/feature/use-hydrate-active-conversation";
import { useRenameChat } from "~/ai-chat/feature/use-rename-chat";
import { chatWindowTitle } from "~/ai-chat/model/chat-window-title";
import { ChatArtifactList } from "~/ai-chat/ui/chat-artifact-list";
import { ChatTabs } from "~/ai-chat/ui/chat-tabs";
import { LlmApiKeyDialog } from "~/ai-chat/ui/llm-api-key-dialog";
import { useInlineTitleEdit } from "~/ai-chat/ui/use-inline-title-edit";
import { routes } from "~/routes";
import { cn } from "~/utils/cn";

export function AiChatFloatingWidget() {
  const { isOpen, close } = useAiChat();
  const location = useLocation();
  const wasOpen = useRef(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const handleClosed = useCallback(() => {
    setIsClosing(false);
  }, []);

  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      setIsClosing(true);
    }
    if (isOpen) {
      setIsClosing(false);
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  if (location.pathname === routes.AiChat) return null;
  if (!isOpen && !isClosing && !wasOpen.current) return null;

  return <AiChatWindow open={isOpen} onClose={close} onClosed={handleClosed} />;
}

function AiChatWindow({ open, onClose, onClosed }: { open: boolean; onClose: () => void; onClosed: () => void }) {
  const { dock, setDock } = useChatDockPosition();
  const { size, isResizing, resizeHandleProps } = useAiChatWindowSize(dock === "right" ? "right" : null);
  const { popoutMode } = useChatPopoutMode();
  const { popoutWindow, isPoppedOut, toggle } = useChatPopout(size, popoutMode);

  useEffect(() => {
    const root = document.documentElement;
    const clearDockCssVars = () => {
      root.style.removeProperty("--ai-chat-dock-right");
      root.style.removeProperty("--ai-chat-dock-bottom");
    };
    if (!open || isPoppedOut) {
      clearDockCssVars();
      return clearDockCssVars;
    }
    if (dock === "right") {
      root.style.setProperty("--ai-chat-dock-right", `${size.width}px`);
      root.style.removeProperty("--ai-chat-dock-bottom");
    } else {
      root.style.setProperty("--ai-chat-dock-bottom", `${size.height}px`);
      root.style.removeProperty("--ai-chat-dock-right");
    }
    return clearDockCssVars;
  }, [open, dock, size.width, size.height, isPoppedOut]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.aiChatDockTransition = "";
    const timeoutId = window.setTimeout(() => {
      delete root.dataset.aiChatDockTransition;
    }, AI_CHAT_DOCK_TRANSITION_MS);
    return () => {
      window.clearTimeout(timeoutId);
      delete root.dataset.aiChatDockTransition;
    };
  }, [open]);

  const onClosedRef = useRef(onClosed);
  useEffect(() => {
    onClosedRef.current = onClosed;
  }, [onClosed]);

  useEffect(() => {
    if (open) return undefined;
    const timeoutId = window.setTimeout(() => {
      onClosedRef.current();
    }, AI_CHAT_DOCK_TRANSITION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  const portalContainer = isPoppedOut && popoutWindow ? popoutWindow.document.body : null;
  const panel = (
    <PortalContainerContext.Provider value={portalContainer}>
      <ChatPanel isPoppedOut={isPoppedOut} onTogglePopout={toggle} onClose={onClose} dock={dock} onSetDock={setDock} />
    </PortalContainerContext.Provider>
  );

  if (isPoppedOut && popoutWindow) {
    return createPortal(
      <div className="bg-background text-foreground flex h-full flex-col">{panel}</div>,
      popoutWindow.document.body,
    );
  }

  const isRight = dock === "right";
  return (
    <aside
      aria-label="Istari Chat"
      style={
        isRight
          ? { width: size.width, top: "var(--infosec-banner-offset, 0px)" }
          : { height: size.height, left: "var(--sidebar-width, 3.5rem)" }
      }
      className={cn(
        "border-border bg-background fixed z-40 flex flex-col shadow-2xl",
        isRight
          ? cn(
              "right-0 bottom-0 max-w-[calc(100vw-2rem)] border-l",
              open
                ? "animate-in slide-in-from-right-4 duration-150"
                : "animate-out slide-out-to-right-4 fade-out-0 duration-150",
            )
          : cn(
              "right-0 bottom-0 max-h-[calc(100vh-2rem)] border-t",
              open
                ? "animate-in slide-in-from-bottom-4 duration-150"
                : "animate-out slide-out-to-bottom-4 fade-out-0 duration-150",
            ),
        isResizing && "transition-none select-none",
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            {...resizeHandleProps}
            role="separator"
            aria-label="Resize chat panel"
            tabIndex={0}
            className={cn(
              "hover:bg-primary/40 focus-visible:bg-primary/60 absolute z-10 touch-none transition-colors outline-none",
              isRight ? "inset-y-0 left-0 w-1.5 cursor-ew-resize" : "inset-x-0 top-0 h-1.5 cursor-ns-resize",
            )}
          />
        </TooltipTrigger>
        <TooltipContent side={isRight ? "left" : "top"}>Drag to resize, double-click to reset</TooltipContent>
      </Tooltip>
      {panel}
    </aside>
  );
}

function ArtifactsToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const label = open ? "Hide artifacts" : "Show artifacts";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          className={cn("px-1.5", open && "text-primary")}
          onClick={onToggle}
          aria-label={label}
          aria-pressed={open}
        >
          <FileText className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

function ChatPanel({
  isPoppedOut,
  onTogglePopout,
  onClose,
  dock,
  onSetDock,
}: {
  isPoppedOut: boolean;
  onTogglePopout: () => void;
  onClose: () => void;
  dock: ChatDockPosition;
  onSetDock: (position: ChatDockPosition) => void;
}) {
  const { messages, savedArtifacts, replaceMessages } = useAiChat();
  const { send, stop, retry, resume, isPending, activity } = useChatSend();
  const { hasKey } = useLlmApiKey();
  useHydrateActiveConversation();
  const chatTabs = useChatTabs();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [artifactsOpen, setArtifactsOpen] = useState(false);
  const artifacts = useConversationArtifacts(messages, savedArtifacts);
  const handleEdit = (messageId: string, content: string) => {
    const index = messages.findIndex((item) => item.id === messageId);
    if (index === -1) return;
    const truncated = messages.slice(0, index);
    replaceMessages(truncated);
    // Pass the truncated history explicitly: `send`'s own `messages` closure is
    // still the pre-edit list this tick, so the discarded turns would otherwise
    // be re-sent to the backend.
    void send(content, truncated);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header
        className="border-border flex h-10 shrink-0 items-center justify-between gap-2 border-b-[0.5px] px-4"
        data-widget-header
      >
        <div className="flex min-w-0 items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className={cn("px-1.5", historyOpen && "text-primary")}
                onClick={() => {
                  setHistoryOpen((prev) => !prev);
                  setArtifactsOpen(false);
                }}
                aria-label={historyOpen ? "Hide chat history" : "Show chat history"}
                aria-pressed={historyOpen}
              >
                <ArrowLeft className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{historyOpen ? "Hide history" : "Show history"}</TooltipContent>
          </Tooltip>
          <Sparkles className="text-muted-foreground size-4 shrink-0" />
          <ChatTitle />
        </div>
        <div className="flex items-center gap-0.5">
          <ArtifactsToggle
            open={artifactsOpen}
            onToggle={() => {
              setArtifactsOpen((prev) => !prev);
              setHistoryOpen(false);
            }}
          />
          <ChatSaveShareControls />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className={cn("px-1.5", hasKey && "text-primary")}
                onClick={openLlmKeyPrompt}
                aria-label={hasKey ? "Update your LLM key" : "Set your LLM key"}
              >
                <KeyRound className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{hasKey ? "Update your LLM key" : "Set your LLM key"}</TooltipContent>
          </Tooltip>
          {!isPoppedOut && (
            <div className="border-input mr-0.5 flex items-center overflow-hidden rounded-md border">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    className={cn("rounded-none px-1.5", dock === "right" && "bg-muted text-foreground")}
                    onClick={() => onSetDock("right")}
                    aria-label="Dock chat to the right"
                    aria-pressed={dock === "right"}
                  >
                    <PanelRight className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Dock right</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    className={cn("rounded-none px-1.5", dock === "bottom" && "bg-muted text-foreground")}
                    onClick={() => onSetDock("bottom")}
                    aria-label="Dock chat to the bottom"
                    aria-pressed={dock === "bottom"}
                  >
                    <PanelBottom className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Dock bottom</TooltipContent>
              </Tooltip>
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className="px-1.5"
                onClick={onTogglePopout}
                aria-label={isPoppedOut ? "Dock chat back into the app" : "Open chat in a separate window"}
              >
                {isPoppedOut ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isPoppedOut ? "Dock back into the app" : "Open in a separate window"}
            </TooltipContent>
          </Tooltip>
          {!isPoppedOut && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="xs" className="px-1.5" onClick={onClose} aria-label="Close Istari AI">
                  <XIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close</TooltipContent>
            </Tooltip>
          )}
        </div>
      </header>
      {historyOpen ? (
        <div className="min-h-0 min-w-0 flex-1">
          <AiChatHistorySidebar onAfterSelect={() => setHistoryOpen(false)} />
        </div>
      ) : artifactsOpen ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <ChatArtifactList
            artifacts={artifacts}
            onOpen={(artifact) => window.open(`/resources/${artifact.resourceId}`, "_blank", "noopener")}
          />
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <ChatTabs
            tabs={chatTabs.tabs}
            onSelect={chatTabs.switchTo}
            onClose={chatTabs.closeTab}
            onNew={chatTabs.startNew}
          />
          <AiChatTranscript
            isPending={isPending}
            activity={activity}
            onSuggestionSelect={(suggestion) => void send(suggestion)}
            onRetry={retry}
            onEdit={handleEdit}
            onApproveResume={resume}
          />
          <AiChatComposer
            key={chatTabs.activeKey}
            onSend={(content) => void send(content)}
            onStop={stop}
            isPending={isPending}
          />
        </div>
      )}
      <LlmApiKeyDialog />
    </div>
  );
}

function ChatTitle() {
  const { activeConversationId, chatTitle, setChatTitle, messages } = useAiChat();
  const { rename } = useRenameChat();
  const titleId = useId();
  const edit = useInlineTitleEdit(chatTitle, (title) => {
    if (activeConversationId !== null) rename(activeConversationId, title);
  });

  const isPersisted = activeConversationId !== null;

  return (
    <input
      id={titleId}
      aria-label="Chat title"
      value={isPersisted ? (edit.draft ?? chatTitle ?? "") : (chatTitle ?? "")}
      onChange={(event) =>
        isPersisted
          ? edit.setDraft(event.target.value)
          : setChatTitle(event.target.value.length > 0 ? event.target.value : null)
      }
      onBlur={isPersisted ? edit.handleBlur : undefined}
      onKeyDown={isPersisted ? edit.handleKeyDown : undefined}
      placeholder={isPersisted ? chatWindowTitle(messages) : "Name this chat…"}
      className={cn(
        "text-foreground placeholder:text-muted-foreground min-w-0 flex-1 truncate bg-transparent",
        "text-sm font-medium outline-none",
      )}
    />
  );
}
